<script setup lang="ts">
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  SERIES_COLORS_DARK,
  SERIES_COLORS_LIGHT,
  type ChartSpec,
} from '../lib/chartSpec';

// Selective registration keeps chart.js tree-shaken: bar + line on category/linear
// scales with legend and hover tooltips is all this card draws.
Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Legend,
  Tooltip,
);

const props = defineProps<{ spec: ChartSpec }>();

const canvas = ref<HTMLCanvasElement | null>(null);
/**
 * The table is the chart's accessibility relief, and in light mode it is mandatory:
 * some palette slots sit below 3:1 contrast on our card surface, and the validated
 * palette rules require visible values or a table view in that case.
 */
const showTable = ref(false);

let chart: Chart | null = null;

const isDark = () => window.matchMedia('(prefers-color-scheme: dark)').matches;

/** Reads theme tokens from CSS so grid/labels follow the page, not a hardcoded hex. */
function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function buildChart() {
  if (!canvas.value) {
    return;
  }
  chart?.destroy();

  const colors = isDark() ? SERIES_COLORS_DARK : SERIES_COLORS_LIGHT;
  const textColor = cssVar('--text-muted');
  const gridColor = cssVar('--border');

  chart = new Chart(canvas.value, {
    data: {
      labels: props.spec.labels,
      datasets: props.spec.series.map((series, index) => {
        // Color follows the series index — fixed slot order, never cycled.
        const color = colors[index % colors.length];
        return series.type === 'line'
          ? {
              type: 'line' as const,
              label: series.name,
              data: series.values,
              borderColor: color,
              backgroundColor: color,
              borderWidth: 2,
              pointRadius: 4,
              pointHoverRadius: 6,
              tension: 0.2,
              spanGaps: false,
            }
          : {
              type: 'bar' as const,
              label: series.name,
              data: series.values,
              backgroundColor: color,
              // Thin marks with rounded data-ends, anchored to the baseline.
              maxBarThickness: 28,
              borderRadius: { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 },
              borderSkipped: 'bottom' as const,
            };
      }),
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: {
          // A single series needs no legend box — the title names it.
          display: props.spec.series.length > 1,
          labels: { color: textColor, boxWidth: 12, boxHeight: 12 },
        },
        tooltip: { intersect: false, mode: 'index' },
      },
      scales: {
        x: {
          ticks: { color: textColor, maxRotation: 45, autoSkip: true },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          ticks: { color: textColor, precision: 0 },
          grid: { color: gridColor },
        },
      },
    },
  });
}

onMounted(() => {
  buildChart();
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', buildChart);
});

onBeforeUnmount(() => {
  window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', buildChart);
  chart?.destroy();
  chart = null;
});

watch(() => props.spec, buildChart);

const legendColors = computed(() => (isDark() ? SERIES_COLORS_DARK : SERIES_COLORS_LIGHT));
</script>

<template>
  <div class="chart">
    <p v-if="spec.title" class="chart__title">{{ spec.title }}</p>

    <div class="chart__plot">
      <canvas ref="canvas" role="img" :aria-label="spec.title ?? 'Grafiek'"></canvas>
    </div>

    <button class="chart__toggle" type="button" @click="showTable = !showTable">
      {{ showTable ? 'Verberg tabel' : 'Toon als tabel' }}
    </button>

    <div v-if="showTable" class="chart__tableWrap">
      <table class="chart__table">
        <thead>
          <tr>
            <th scope="col"></th>
            <th v-for="(series, index) in spec.series" :key="series.name" scope="col">
              <span
                class="chart__swatch"
                :style="{ background: legendColors[index % legendColors.length] }"
                aria-hidden="true"
              ></span>
              {{ series.name }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(label, row) in spec.labels" :key="label + row">
            <th scope="row">{{ label }}</th>
            <td v-for="series in spec.series" :key="series.name">
              {{ series.values[row] ?? '—' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.chart {
  padding: 12px;
  border-top: 1px solid var(--border);
}

.chart__title {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
}

.chart__plot {
  position: relative;
  height: 260px;
}

.chart__toggle {
  margin-top: 8px;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 12px;
  color: var(--accent);
  cursor: pointer;
  text-decoration: underline;
}

.chart__tableWrap {
  margin-top: 8px;
  overflow-x: auto;
}

.chart__table {
  border-collapse: collapse;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.chart__table th,
.chart__table td {
  padding: 4px 10px;
  border-bottom: 1px solid var(--border);
  text-align: right;
}

.chart__table th[scope='row'],
.chart__table thead th {
  text-align: left;
  font-weight: 500;
  color: var(--text-muted);
}

.chart__swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  margin-right: 5px;
  vertical-align: -1px;
}
</style>
